/**
 * Form pipeline step 2: Server actions for form submissions
 * Split into individual files with barrel index.ts
 */

import { getAllFormSchemaFields } from '@core-engine/schema/field-helpers/index.js'
import type {
  FormField,
  FormSchema,
  GeneratedFile,
  GeneratorOptions
} from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { toCamelCase, toKebabCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionDir } from '@next-adapter/config/paths.js'
import {
  afterHook,
  beforeHook,
  lifecycleContext,
  lifecycleHookImport
} from '@next-adapter/generators/actions/lifecycle-hook-emissions.js'

interface StepResult {
  files: GeneratedFile[]
}

const SEARCHABLE_FORM_FIELD_TYPES = new Set<FormField['type']>([
  'text',
  'textarea',
  'email',
  'phone',
  'url',
  'select',
  'radio'
])

const SORTABLE_FORM_FIELD_TYPES = new Set<FormField['type']>([
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'url',
  'date',
  'select',
  'radio',
  'checkbox',
  'timezone'
])

function getSearchableSubmissionFields(fields: FormField[]): string[] {
  return Array.from(
    new Set(
      fields
        .filter((field): field is FormField & { name: string } => Boolean(field.name))
        .filter((field) => SEARCHABLE_FORM_FIELD_TYPES.has(field.type))
        .map((field) => field.name)
    )
  )
}

function getSortableSubmissionFields(schema: FormSchema, fields: FormField[]): string[] {
  if (schema.columns && schema.columns.length > 0) {
    return Array.from(
      new Set(
        schema.columns.filter((column) => column.sortable).map((column) => column.accessorKey)
      )
    )
  }

  return Array.from(
    new Set(
      fields
        .filter((field): field is FormField & { name: string } => Boolean(field.name))
        .filter((field) => SORTABLE_FORM_FIELD_TYPES.has(field.type))
        .map((field) => field.name)
    )
  )
}

function generateMailchimpCode(schema: FormSchema, fields: FormField[]): string {
  if (!schema.mailchimp) return ''

  let emailFieldName: string
  if (typeof schema.mailchimp === 'object' && schema.mailchimp.emailField) {
    emailFieldName = schema.mailchimp.emailField
  } else {
    const emailField = fields.find((f) => f.type === 'email')
    if (!emailField || !emailField.name) {
      console.warn(
        `Warning: Form "${schema.name}" has mailchimp enabled but no email field found. Skipping.`
      )
      return ''
    }
    emailFieldName = emailField.name
  }

  return `\n    // Add to Mailchimp audience (fire-and-forget, non-blocking)
    addToMailchimpAudience(data.${emailFieldName})`
}

export function generateFormActions(
  schema: FormSchema,
  actionsDir: string,
  options: GeneratorOptions
): StepResult {
  const schemaActionDir = resolveSchemaActionDir(actionsDir, schema.name, options.outputNamespace)
  const formName = schema.name
  const tableName = `${toCamelCase(formName)}Submissions`
  const pascal = toPascalCase(formName)
  const kebab = toKebabCase(formName)
  const formSettingsKey = kebab
  const fields = getAllFormSchemaFields(schema)
  const searchFields = getSearchableSubmissionFields(fields)
  const sortableFields = getSortableSubmissionFields(schema, fields)
  const nextMajor = options.nextMajorVersion ?? 16
  const cacheInvalidationFn = nextMajor >= 16 ? 'updateTag' : 'revalidateTag'
  const cacheReadImport =
    nextMajor >= 16
      ? `import { cacheLife, cacheTag } from 'next/cache'`
      : `import { unstable_cacheTag as cacheTag } from 'next/cache'`
  const cacheLifeLine = nextMajor >= 16 ? `\n  cacheLife('max')` : ''
  const idPlaceholder = '$' + '{id}'

  const dataFields = fields
    .map((f) => {
      let tsType = 'string'
      if (f.type === 'number') tsType = 'number'
      else if (f.type === 'checkbox') tsType = f.options?.length ? 'string[]' : 'boolean'
      else if (f.type === 'multiselect') tsType = 'string[]'
      else if (f.type === 'list') {
        tsType = f.fields && f.fields.length > 0 ? 'Record<string, unknown>[]' : 'string[]'
      }
      const isReq = f.required && !f.condition
      const opt = isReq ? '' : '?'
      const nullable = isReq ? '' : ' | null'
      return `  ${f.name}${opt}: ${tsType}${nullable}`
    })
    .join('\n')

  const validation = fields
    .filter((f) => f.required && !f.condition)
    .map((f) => {
      const error = `return { success: false, error: '${f.label || f.name} is required' }`
      let check: string
      if (f.type === 'checkbox' && f.options?.length) {
        check = `!Array.isArray(data.${f.name}) || data.${f.name}.length === 0`
      } else if (f.type === 'checkbox') {
        check = `data.${f.name} !== true`
      } else if (f.type === 'number') {
        check = `data.${f.name} === undefined || data.${f.name} === null`
      } else if (f.type === 'multiselect' || f.type === 'list') {
        check = `!Array.isArray(data.${f.name}) || data.${f.name}.length === 0`
      } else {
        check = `data.${f.name} === undefined || data.${f.name} === null || data.${f.name} === ''`
      }
      return `if (${check}) {\n      ${error}\n    }`
    })
    .join('\n    ')

  const mailchimpImport = schema.mailchimp
    ? `\nimport { addToMailchimpAudience } from '@admin/actions/mailchimp/add-to-mailchimp-audience'`
    : ''
  const mailchimpCall = generateMailchimpCode(schema, fields)

  const emailField = fields.find((f) => f.type === 'email')
  const submitterEmailField = emailField?.name ?? null
  const schemaNotificationFallback = schema.notificationEmail
    ? `[${JSON.stringify(schema.notificationEmail)}]`
    : 'process.env.NOTIFICATION_EMAIL\n        ? [process.env.NOTIFICATION_EMAIL]\n        : []'

  const sortableColumnsMap = sortableFields
    .map((field) => `  '${field}': ${tableName}.${field}`)
    .join(',\n')

  const searchBlock =
    searchFields.length > 0
      ? `    const search = filters?.search
    if (search && typeof search === 'string' && search.trim()) {
      const searchTerm = \`%\${search.trim().toLowerCase()}%\`
      conditions.push(
        or(
${searchFields.map((field) => `          ilike(${tableName}.${field}, searchTerm)`).join(',\n')}
        )
      )
    }
`
      : ''

  const getSubmissionsDrizzleImports = ['and', 'asc', 'desc', 'gte', 'lt', 'sql']
  if (searchFields.length > 0) {
    getSubmissionsDrizzleImports.push('ilike', 'or')
  }

  const typesContent = `export interface ${pascal}SubmissionData {
  id: string
${dataFields}
  ipAddress: string | null
  userAgent: string | null
  submittedAt: string
  createdAt: string
  updatedAt: string
}

export interface ${pascal}SubmissionsResponse {
  submissions: ${pascal}SubmissionData[]
  total: number
}

export interface Get${pascal}SubmissionsFilters {
  search?: string
  submittedAtFrom?: string
  submittedAtTo?: string
  limit?: number
  offset?: number
  orderBy?: string
  orderDirection?: 'asc' | 'desc'
}

export interface Create${pascal}SubmissionInput {
${dataFields}
  ipAddress?: string
  userAgent?: string
}

export interface Create${pascal}SubmissionResult {
  success: boolean
  error?: string
  submission?: ${pascal}SubmissionData
}

export interface Delete${pascal}SubmissionResult {
  success: boolean
  error?: string
  count?: number
}

export const CACHE_TAG = '${formName}:submissions:all'
export const CACHE_TAG_BY_ID = (id: string) => \`${formName}:submissions:id:${idPlaceholder}\`
`

  const createLifecycleContext = lifecycleContext(
    formName,
    `,\n      input: data as unknown as Record<string, unknown>,\n      changedFields`,
    {
      schemaType: 'form',
      sourceType: 'server_action',
      userExpression: 'session?.user ?? null'
    }
  )
  const createResultLifecycleContext = lifecycleContext(
    formName,
    `,\n      entityId: created${pascal}Submission.id,\n      input: data as unknown as Record<string, unknown>,\n      data: created${pascal}Submission as unknown as Record<string, unknown>,\n      changedFields`,
    {
      schemaType: 'form',
      sourceType: 'server_action',
      userExpression: 'session?.user ?? null'
    }
  )
  const deleteLifecycleContext = lifecycleContext(
    formName,
    `,\n      entityId: id,\n      changedFields: []`,
    {
      schemaType: 'form',
      sourceType: 'admin_ui',
      userExpression: 'session?.user ?? null'
    }
  )

  const getSubmissionsContent = `'use server'

import { ${getSubmissionsDrizzleImports.sort().join(', ')} } from 'drizzle-orm'
import db from '@admin/db'
import { ${tableName} } from '@admin/db/schema'
${cacheReadImport}
import { CACHE_TAG } from './types'
import type {
  Get${pascal}SubmissionsFilters,
  ${pascal}SubmissionsResponse,
  ${pascal}SubmissionData
} from './types'

function getExclusiveDateUpperBound(value?: string): string | null {
  if (!value) return null

  const date = new Date(\`\${value}T00:00:00.000Z\`)
  if (Number.isNaN(date.getTime())) return null

  date.setUTCDate(date.getUTCDate() + 1)
  return date.toISOString().slice(0, 10)
}

const SORTABLE_COLUMNS = {
${sortableColumnsMap}
} as const

export async function get${pascal}Submissions(
  filters?: Get${pascal}SubmissionsFilters
): Promise<${pascal}SubmissionsResponse> {
  'use cache'${cacheLifeLine}
  cacheTag(CACHE_TAG)

  try {
    const conditions = []
${searchBlock}    if (filters?.submittedAtFrom) {
      conditions.push(gte(${tableName}.submittedAt, filters.submittedAtFrom))
    }
    const submittedAtExclusiveTo = getExclusiveDateUpperBound(filters?.submittedAtTo)
    if (submittedAtExclusiveTo) {
      conditions.push(lt(${tableName}.submittedAt, submittedAtExclusiveTo))
    }

    const query = db.select().from(${tableName})

    const sortColumn = filters?.orderBy && filters.orderBy in SORTABLE_COLUMNS
      ? SORTABLE_COLUMNS[filters.orderBy as keyof typeof SORTABLE_COLUMNS]
      : ${tableName}.submittedAt
    const sortFn =
      filters?.orderBy && filters.orderBy in SORTABLE_COLUMNS
        ? filters.orderDirection === 'desc'
          ? desc
          : asc
        : desc
    const orderClause = sortFn(sortColumn)

    const orderedQuery = conditions.length > 0
      ? query.where(and(...conditions)).orderBy(orderClause)
      : query.orderBy(orderClause)

    const limit = filters?.limit
    const offset = filters?.offset
    const isPaginated = typeof limit === 'number' && limit > 0
    let results: unknown[]
    if (isPaginated) {
      const paginatedQuery = typeof offset === 'number' && offset > 0
        ? orderedQuery.limit(limit).offset(offset)
        : orderedQuery.limit(limit)
      results = await paginatedQuery
    } else {
      results = await orderedQuery
    }

    const total = isPaginated
      ? Number(
          (
            await db
              .select({ count: sql\`count(*)\` })
              .from(${tableName})
              .where(conditions.length > 0 ? and(...conditions) : undefined)
          )[0]?.count ?? 0
        )
      : results.length

    return {
      submissions: results as ${pascal}SubmissionData[],
      total
    }
  } catch (error) {
    console.error('Error fetching ${formName} submissions:', error)
    throw new Error('Failed to fetch ${formName} submissions')
  }
}
`

  const getSubmissionContent = `'use server'

import { eq } from 'drizzle-orm'
import db from '@admin/db'
import { ${tableName} } from '@admin/db/schema'
${cacheReadImport}
import { CACHE_TAG, CACHE_TAG_BY_ID } from './types'
import type { ${pascal}SubmissionData } from './types'

export async function get${pascal}Submission(id: string): Promise<${pascal}SubmissionData | null> {
  'use cache'${cacheLifeLine}
  cacheTag(CACHE_TAG_BY_ID(id))
  cacheTag(CACHE_TAG)

  try {
    const [submission] = await db
      .select()
      .from(${tableName})
      .where(eq(${tableName}.id, id))
      .limit(1)
    return (submission as ${pascal}SubmissionData) ?? null
  } catch (error) {
    console.error(\`Error fetching ${formName} submission \${id}:\`, error)
    throw new Error('Failed to fetch ${formName} submission')
  }
}
`

  const createContent = `'use server'

import { getSession } from '@admin/auth/middleware'
import db from '@admin/db'
import { ${tableName} } from '@admin/db/schema'
import { getFormSettings } from '@admin/actions/forms'
import { sendFormEmails } from '@admin/actions/email/form-delivery'
import { getTouchedFieldNames } from '@admin/utils/audit/audit'
import { ${cacheInvalidationFn} } from 'next/cache'
${mailchimpImport}
import { CACHE_TAG, CACHE_TAG_BY_ID } from './types'
${lifecycleHookImport()}
import type { Create${pascal}SubmissionInput, Create${pascal}SubmissionResult, ${pascal}SubmissionData } from './types'

export async function create${pascal}Submission(
  data: Create${pascal}SubmissionInput
): Promise<Create${pascal}SubmissionResult> {
  try {
    const session = await getSession()
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const changedFields = getTouchedFieldNames(data as unknown as Record<string, unknown>)
    const lifecycleContext = ${createLifecycleContext}
    ${beforeHook('beforeCreate', 'lifecycleContext')}

    ${validation}

    const [submission] = await db
      .insert(${tableName})
      .values({
        ...data,
        submittedAt: now
      })
      .returning()
    const created${pascal}Submission = submission as ${pascal}SubmissionData

    const settings = await getFormSettings('${formSettingsKey}')
    const notificationEmails = settings?.notificationEmails
      ? settings.notificationEmails.split(',').map((e: string) => e.trim()).filter(Boolean)
      : ${schemaNotificationFallback}

    await sendFormEmails({
      formName: '${formName}',
      formLabel: '${schema.label}',
      notificationEmails,
      submitterEmail: ${submitterEmailField ? `data.${submitterEmailField}` : 'null'},
      submissionData: { ...data },
      submittedAt: created${pascal}Submission.submittedAt,
    })${mailchimpCall}

    ${cacheInvalidationFn}(CACHE_TAG)
    ${cacheInvalidationFn}(CACHE_TAG_BY_ID(created${pascal}Submission.id))
    const created${pascal}SubmissionContext = ${createResultLifecycleContext}
    ${afterHook('afterCreate', `created${pascal}SubmissionContext`)}

    return {
      success: true,
      submission: created${pascal}Submission
    }
  } catch (error) {
    console.error('Error creating ${formName} submission:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create ${formName} submission'
    }
  }
}
`

  const deleteContent = `'use server'

import { getSession } from '@admin/auth/middleware'
import { eq } from 'drizzle-orm'
import db from '@admin/db'
import { ${tableName} } from '@admin/db/schema'
import { ${cacheInvalidationFn} } from 'next/cache'
import { CACHE_TAG, CACHE_TAG_BY_ID } from './types'
${lifecycleHookImport()}
import type { Delete${pascal}SubmissionResult } from './types'

export async function delete${pascal}Submission(id: string): Promise<Delete${pascal}SubmissionResult> {
  try {
    const session = await getSession()
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const lifecycleContext = ${deleteLifecycleContext}
    ${beforeHook('beforeDelete', 'lifecycleContext')}

    await db.delete(${tableName}).where(eq(${tableName}.id, id))
    ${cacheInvalidationFn}(CACHE_TAG)
    ${cacheInvalidationFn}(CACHE_TAG_BY_ID(id))
    ${afterHook('afterDelete', 'lifecycleContext')}
    return { success: true }
  } catch (error) {
    console.error(\`Error deleting ${formName} submission \${id}:\`, error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete ${formName} submission'
    }
  }
}
`

  const deleteBulkContent = `'use server'

import { getSession } from '@admin/auth/middleware'
import { eq, or } from 'drizzle-orm'
import db from '@admin/db'
import { ${tableName} } from '@admin/db/schema'
import { ${cacheInvalidationFn} } from 'next/cache'
import { CACHE_TAG, CACHE_TAG_BY_ID } from './types'
${lifecycleHookImport()}
import type { Delete${pascal}SubmissionResult } from './types'

export async function deleteBulk${pascal}Submissions(ids: string[]): Promise<Delete${pascal}SubmissionResult> {
  try {
    if (ids.length === 0) return { success: true, count: 0 }
    const session = await getSession()
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const lifecycleContexts = ids.map((id) => (${deleteLifecycleContext}))

    for (const lifecycleContext of lifecycleContexts) {
      ${beforeHook('beforeDelete', 'lifecycleContext')}
    }

    await db.delete(${tableName}).where(
      or(...ids.map(id => eq(${tableName}.id, id)))
    )
    ${cacheInvalidationFn}(CACHE_TAG)
    for (const id of ids) {
      ${cacheInvalidationFn}(CACHE_TAG_BY_ID(id))
    }
    for (const lifecycleContext of lifecycleContexts) {
      ${afterHook('afterDelete', 'lifecycleContext')}
    }
    return { success: true, count: ids.length }
  } catch (error) {
    console.error(\`Error bulk deleting ${formName} submissions:\`, error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to bulk delete ${formName} submissions'
    }
  }
}
`

  const hasExport = schema.actions?.export === true

  const exportCsvContent = `'use server'

import { get${pascal}Submissions } from './get-${kebab}-submissions'

export async function export${pascal}SubmissionsCSV(): Promise<string> {
  const { submissions } = await get${pascal}Submissions()
  if (submissions.length === 0) return ''
  const headers = Object.keys(submissions[0]).join(',')
  const rows = submissions.map((sub) =>
    Object.values(sub)
      .map((val: unknown) => {
        if (val === null || val === undefined) return ''
        const str = String(val)
        if (str.includes(',') || str.includes('"') || str.includes('\\n')) {
          return \`"\${str.replace(/"/g, '""')}"\`
        }
        return str
      })
      .join(',')
  )
  return [headers, ...rows].join('\\n')
}
`

  const exportBarrelLine = hasExport
    ? `\nexport { export${pascal}SubmissionsCSV } from './export-${kebab}-submissions-csv'`
    : ''

  const barrelContent = `export type {
  ${pascal}SubmissionData,
  ${pascal}SubmissionsResponse,
  Get${pascal}SubmissionsFilters,
  Create${pascal}SubmissionInput,
  Create${pascal}SubmissionResult,
  Delete${pascal}SubmissionResult
} from './types'
export { get${pascal}Submissions } from './get-${kebab}-submissions'
export { get${pascal}Submission } from './get-${kebab}-submission'
export { create${pascal}Submission } from './create-${kebab}-submission'
export { delete${pascal}Submission } from './delete-${kebab}-submission'
export { deleteBulk${pascal}Submissions } from './delete-bulk-${kebab}-submissions'${exportBarrelLine}
export { CACHE_TAG, CACHE_TAG_BY_ID } from './types'
`

  const files: { name: string; content: string }[] = [
    { name: 'types.ts', content: typesContent },
    { name: `get-${kebab}-submissions.ts`, content: getSubmissionsContent },
    { name: `get-${kebab}-submission.ts`, content: getSubmissionContent },
    { name: `create-${kebab}-submission.ts`, content: createContent },
    { name: `delete-${kebab}-submission.ts`, content: deleteContent },
    { name: `delete-bulk-${kebab}-submissions.ts`, content: deleteBulkContent }
  ]
  if (hasExport) {
    files.push({ name: `export-${kebab}-submissions-csv.ts`, content: exportCsvContent })
  }
  files.push({ name: 'index.ts', content: barrelContent })

  return {
    files: files.map((file) => createGeneratedFile(`${schemaActionDir}/${file.name}`, file.content))
  }
}
